import {
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import PanelLoader from '@/components/PanelLoader';

type PanelLoadingContextValue = {
    isLoading: boolean;
    setLoading: (value: boolean) => void;
};

const PanelLoadingContext = createContext<PanelLoadingContextValue | undefined>(undefined);

type PanelLoadingBoundaryProps = {
    children: ReactNode;
    label?: string;
    className?: string;
    wrapperClassName?: string;
};

/**
 * パネルに共通のローディングオーバーレイを提供するコンポーネント。
 * 子コンポーネントは usePanelLoading フックを通じてローディング状態を制御できる。
 */
const PanelLoadingBoundary = ({
    children,
    label = 'Loading…',
    className = '',
    wrapperClassName = '',
}: PanelLoadingBoundaryProps) => {
    const [isLoading, setIsLoading] = useState(true);
    const touchedRef = useRef(false);

    const updateLoading = useCallback((value: boolean) => {
        touchedRef.current = true;
        setIsLoading(value);
    }, []);

    const contextValue = useMemo(
        () => ({
            isLoading,
            setLoading: updateLoading,
        }),
        [isLoading, updateLoading],
    );

    useEffect(() => {
        if (touchedRef.current) return;
        if (typeof window === 'undefined') {
            setIsLoading(false);
            return;
        }
        const frame = window.requestAnimationFrame(() => {
            if (!touchedRef.current) {
                setIsLoading(false);
            }
        });
        return () => {
            window.cancelAnimationFrame(frame);
        };
    }, []);

    return (
        <PanelLoadingContext.Provider value={contextValue}>
            <div className={`relative w-full h-full ${wrapperClassName}`}>
                <div
                    className={`h-full w-full transition-opacity duration-150 ${
                        isLoading ? 'opacity-0 pointer-events-none' : 'opacity-100'
                    } ${className}`}
                    aria-hidden={isLoading}
                >
                    {children}
                </div>
                {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80">
                        <PanelLoader label={label} />
                    </div>
                )}
            </div>
        </PanelLoadingContext.Provider>
    );
};

export const usePanelLoading = (): PanelLoadingContextValue => {
    const ctx = useContext(PanelLoadingContext);
    if (!ctx) {
        return {
            isLoading: false,
            setLoading: () => {},
        };
    }
    return ctx;
};

export default PanelLoadingBoundary;
